import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FORMAT_FOR_DATES_LONG } from 'src/app/core/helpers/ui/ui.constant';
import { User } from 'src/app/core/interfaces/api/user.interface';
import { ModalWithAction } from 'src/app/core/interfaces/ui/bootstrap-modal.interface';
import { NgxDatatableConfig } from 'src/app/core/interfaces/ui/ngx-datatable.interface';
import { UserService } from 'src/app/core/services/api/user.service';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { FilterService } from 'src/app/core/services/ui/filter.service';
import { NgxDatatableService } from 'src/app/core/services/ui/ngx-datatable.service';

@Component({
  selector: 'user-block-history-modal',
  templateUrl: './user-block-history-modal.component.html',
  providers: [NgxDatatableService, FilterService],
})
export class UserBlockHistoryModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inline: boolean = false;
  @Input() user: User | null = null;

  private unsubscribe$: Subject<boolean> = new Subject<boolean>();
  public FORMAT_FOR_DATES_LONG = FORMAT_FOR_DATES_LONG;
  public selectedUser: User | undefined;
  public data$: Observable<any[]> = of([]);
  public config$: Observable<Partial<NgxDatatableConfig>> = of({});

  private userId = '';

  @ViewChild('createdAt', { static: true })
  public createdAtTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('action', { static: true })
  public actionTemplate?: TemplateRef<HTMLElement>;

  constructor(
    private _bsModalService: BootstrapModalService<ModalWithAction<User>>,
    private _datatable: NgxDatatableService<any, object>,
    private _userService: UserService,
    private _filterService: FilterService<object>
  ) {}

  ngOnInit(): void {
    this.setTableConfig();
    this.config$ = this._datatable.config$;
    this.data$ = this._datatable.data$;
    this._datatable.setDataSearchFunction((filter: any) => {
      const { id, ...rest } = filter;
      return this._userService.getUserBlockHistory(id, rest);
    });
    if (this.inline) {
      this.setUser(this.user);
      return;
    }
    this.subscribeToModalData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.inline && changes['user'] && !changes['user'].firstChange) {
      this.setUser(this.user);
    }
  }

  private setUser(user: User | null | undefined): void {
    this.selectedUser = user ?? undefined;
    this.userId = user?._id ?? '';
    this.loadHistory();
  }

  private subscribeToModalData(): void {
    this._bsModalService
      .getDataIssued()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ selectedRow }) => this.setUser(selectedRow),
        error: () => this.closeModal(),
      });
  }

  private loadHistory(): void {
    if (!this.userId) return;
    this._filterService.updateFilterDataBehavior({
      id: this.userId,
      page: 1,
      limit: 10,
    });
  }

  public onChangeLimit(limit: number): void {
    this._datatable.changeLimit(limit);
  }

  public onChangePage(page: number): void {
    this._datatable.changePage(page);
  }

  private setTableConfig(): void {
    this._datatable.setTableConfig({
      loadingIndicator: false,
      emptyMessage: 'userManagement.block.noHistory',
      sort: { prop: 'createdAt', dir: 'desc' },
      columns: [
        {
          name: 'userManagement.block.historyDate',
          cellTemplate: this.createdAtTemplate ?? undefined,
          width: 170,
        },
        {
          name: 'userManagement.block.historyAction',
          cellTemplate: this.actionTemplate ?? undefined,
          width: 130,
        },
        {
          name: 'userManagement.block.historyReason',
          prop: 'reason',
          width: 250,
        },
        {
          name: 'userManagement.block.historyPerformedBy',
          prop: 'performedBy.username',
          width: 150,
        },
      ],
    });
  }

  public closeModal(): void {
    this._bsModalService.closeModal();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }
}
